import { useEffect, useState } from "react";
import { Loader2, Megaphone, Send, Trash2 } from "lucide-react";
import { PageLoader } from "@/components/brand/PageLoader";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { supabase } from "@/lib/supabase";

type Audience = "all" | "players" | "venues";
type Severity = "info" | "warning" | "critical";

interface Announcement {
  id: string;
  title: string;
  body: string;
  audience: Audience;
  severity: Severity;
  starts_at: string;
  ends_at: string | null;
  created_at: string;
}

const AUDIENCE_LABEL: Record<Audience, string> = {
  all: "Everyone",
  players: "Players only",
  venues: "Venues only",
};

const SEVERITY_BG: Record<Severity, string> = {
  info: "rgba(96,165,250,0.15)",
  warning: "rgba(251,191,36,0.15)",
  critical: "rgba(248,113,113,0.15)",
};
const SEVERITY_COLOR: Record<Severity, string> = {
  info: "#60a5fa",
  warning: "#fbbf24",
  critical: "#f87171",
};

export function AdminAnnouncements() {
  const [list, setList] = useState<Announcement[] | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState<Audience>("all");
  const [severity, setSeverity] = useState<Severity>("info");
  const [endsAt, setEndsAt] = useState("");
  const [posting, setPosting] = useState(false);

  async function load() {
    const { data, error } = await supabase
      .from("announcements")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) {
      toast.error("Failed to load: " + error.message);
      setList([]);
      return;
    }
    setList((data ?? []) as Announcement[]);
  }

  useEffect(() => {
    load();
  }, []);

  async function onPost(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setPosting(true);
    const { error } = await supabase.from("announcements").insert({
      title: title.trim(),
      body: body.trim(),
      audience,
      severity,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
    });
    setPosting(false);
    if (error) {
      toast.error("Post failed: " + error.message);
      return;
    }
    toast.success("Announcement posted.");
    setTitle("");
    setBody("");
    setEndsAt("");
    await load();
  }

  async function onDelete(id: string) {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    const { error } = await supabase.from("announcements").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Deleted.");
    await load();
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Announcements
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Announcements
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Active announcements appear in the mobile app and venue
          dashboard. Keep them short - they're banners, not blog posts.
        </p>
      </header>

      {/* Composer */}
      <form
        onSubmit={onPost}
        className="mb-10 rounded-2xl border border-border bg-card p-5"
      >
        <div className="mb-4 flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-volt" />
          <h2 className="text-sm font-bold uppercase tracking-wider">
            New announcement
          </h2>
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="We're live in Sydney"
              maxLength={80}
              required
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold">Message</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder="First 20 venues are now onboarding. Apply at whistll.com/venues if you'd like to be next."
              maxLength={400}
              required
            />
            <p className="mt-1 text-right text-[10px] text-muted-foreground">
              {body.length}/400
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-xs font-semibold">Audience</label>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value as Audience)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">Everyone</option>
                <option value="players">Players only</option>
                <option value="venues">Venues only</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as Severity)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold">
                Ends (optional)
              </label>
              <Input
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>
          </div>

          <Button type="submit" size="lg" disabled={posting} className="self-start">
            {posting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Posting…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Post announcement
              </>
            )}
          </Button>
        </div>
      </form>

      {/* List */}
      <section>
        <h2 className="mb-4 text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Recent
        </h2>
        {list === null ? (
          <PageLoader />
        ) : list.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border bg-card/40 py-10 text-center text-sm text-muted-foreground">
            No announcements yet.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.map((a) => {
              const active =
                new Date(a.starts_at) <= new Date() &&
                (a.ends_at === null || new Date(a.ends_at) > new Date());
              return (
                <li
                  key={a.id}
                  className="rounded-xl border border-border bg-card p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate font-semibold">{a.title}</h3>
                        <span
                          className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize"
                          style={{
                            background: SEVERITY_BG[a.severity],
                            color: SEVERITY_COLOR[a.severity],
                          }}
                        >
                          {a.severity}
                        </span>
                        {active ? (
                          <span className="shrink-0 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
                            Live
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {a.body}
                      </p>
                      <p className="mt-1.5 text-[10px] text-muted-foreground">
                        {AUDIENCE_LABEL[a.audience]} ·{" "}
                        {new Date(a.created_at).toLocaleString()}
                        {a.ends_at && (
                          <> · ends {new Date(a.ends_at).toLocaleString()}</>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => onDelete(a.id)}
                      aria-label="Delete announcement"
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
